import { useField, useFormikContext, FieldInputProps } from 'formik'
import React, { forwardRef } from 'react'
import Text from 'particles/Text'
import Icon from 'particles/Icon'
import { StyledDatePickerContainer, StyledReadOnlyContainer, DatePickerInputContainer, ErrorContainer } from './style'
import { useTranslation } from 'react-i18next'
import DatePicker, { ReactDatePickerProps } from 'react-datepicker'
import { Country } from 'services/api/constants'
import { getTimeZonedDateNow, parseBackendDate } from 'services/dates'
import IconButton from 'particles/Button/IconButton'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import Tooltip from 'particles/Tooltip'
import CommentContainer from 'components/Shared/Comment'

export type DatePickerSize = 's' | 'm' | 'l'

export type PopperPlacementPositions =
    | 'auto'
    | 'auto-left'
    | 'auto-right'
    | 'bottom'
    | 'bottom-end'
    | 'bottom-start'
    | 'left'
    | 'left-end'
    | 'left-start'
    | 'right'
    | 'right-end'
    | 'right-start'
    | 'top'
    | 'top-end'
    | 'top-start'

interface BaseDatePickerProps {
    name: string
    country?: Country
    onChange?: (date: Date | null) => void
    label?: string
    subtext?: string
    tooltipContext?: { title?: string; label?: string }
    placeholder?: string
    size?: DatePickerSize
    readOnlyEmptyText?: string
    isDisabled?: boolean
    isReadOnly?: boolean
    isRequired?: boolean
    isCommentable?: boolean
    isCommented?: boolean
    onLabelClick?: () => void
    showError?: boolean
    showErrorText?: boolean
    validateByTouch?: boolean // set to false if you want to use an external error state to control error display. It will show errors without requiring the field to be touched.
    showTime?: boolean
    width?: string
    className?: string
    identifier?: string
    popperPlacement?: PopperPlacementPositions
    labelSize?: LabelSize
    link?: LinkConfig
    tooltip?: string
}

export interface DatePickerProps
    extends BaseDatePickerProps,
        Omit<ReactDatePickerProps | FieldInputProps<string>, 'name' | 'size' | 'onChange'> {
    isMetaError?: boolean
    showClearButton?: boolean
    date?: string
    onClickClear: () => void
    error?: string
}

const DateInput = forwardRef<HTMLInputElement, DatePickerProps>(
    (
        {
            country,
            onChange,
            label = '',
            subtext,
            tooltipContext,
            placeholder,
            size = 's',
            readOnlyEmptyText,
            isDisabled = false,
            isReadOnly = false,
            isRequired = false,
            isCommentable = false,
            isCommented = false,
            onLabelClick,
            showErrorText = true, // set false if want to use external error text display. ex: DateInputRange
            showTime,
            width,
            identifier,
            popperPlacement = 'bottom-start',
            isMetaError,
            showClearButton,
            date,
            onClickClear,
            error,
            labelSize,
            link,
            tooltip,
            className,
            ...props // merge date picker props and formik field props into single props
        },
        ref,
    ) => {
        const [t] = useTranslation()
        return (
            <>
                {isReadOnly ? (
                    <StyledDatePickerContainer className={className} ref={ref}>
                        {(label || subtext) && (
                            <CommentContainer identifier={identifier}>
                                <Label
                                    title={label}
                                    subtext={subtext}
                                    isRequired={isRequired}
                                    size={labelSize ? labelSize : size}
                                    isCommentable={isCommentable}
                                    isCommented={isCommented}
                                    onClick={onLabelClick}
                                    link={link}
                                    tooltip={tooltip}
                                />
                            </CommentContainer>
                        )}
                        <StyledReadOnlyContainer width={width} size={size}>
                            <Icon
                                name="calendarBlank"
                                size="20px"
                                color={isDisabled ? 'disable' : date ? 'primary' : 'disable'}
                            />
                            <Text color={date ? 'primary' : 'disable'} scale={date ? 'bodyS700' : 'bodyS400'}>
                                {date
                                    ? date
                                    : readOnlyEmptyText
                                      ? readOnlyEmptyText
                                      : t('placeholderText.noSelectedDate')}
                            </Text>
                        </StyledReadOnlyContainer>
                    </StyledDatePickerContainer>
                ) : (
                    <StyledDatePickerContainer className={className} ref={ref}>
                        {(label || subtext) && (
                            <CommentContainer identifier={identifier}>
                                <Label
                                    title={label}
                                    subtext={subtext}
                                    isRequired={isRequired}
                                    size={labelSize ? labelSize : size}
                                    isCommentable={isCommentable}
                                    isCommented={isCommented}
                                    onClick={onLabelClick}
                                    link={link}
                                    tooltip={tooltip}
                                />
                            </CommentContainer>
                        )}
                        <ErrorContainer>
                            <DatePickerInputContainer
                                isError={isMetaError}
                                isDisabled={isDisabled}
                                width={width}
                                size={size}
                                date={date}
                            >
                                <div className={`date-input-area ${isDisabled ? 'disable' : ''}`}>
                                    <div className="icon-container">
                                        <Icon
                                            name="calendarBlank"
                                            size="20px"
                                            color={isDisabled ? 'disable' : date ? 'primary' : 'disable'}
                                        />
                                    </div>

                                    <div className="date-icon-container">
                                        <DatePicker
                                            {...props}
                                            dayClassName={dateValue => {
                                                if (country) {
                                                    // To style the current date based on country's timezone
                                                    const now = getTimeZonedDateNow(country)
                                                    return dateValue.toDateString() === now.toDateString()
                                                        ? 'react-datepicker__day--today'
                                                        : 'react-datepicker__day--normal'
                                                }
                                                return ''
                                            }}
                                            autoComplete={'off'}
                                            //adding default fixedHeight to fix the date-picker position top increasing month position moving incorrect behavior bug
                                            //ref: https://github.com/Hacker0x01/react-datepicker/issues/361
                                            fixedHeight
                                            selected={
                                                date
                                                    ? new Date(date)
                                                    : country
                                                      ? getTimeZonedDateNow(country)
                                                      : undefined
                                            }
                                            value={date}
                                            placeholderText={
                                                placeholder ? placeholder : t('placeholderText.noSelectedDate')
                                            }
                                            onChange={(date: Date | null): void =>
                                                onChange ? onChange(date) : undefined
                                            }
                                            readOnly={isDisabled}
                                            maxDate={new Date('2099/12/31')}
                                            popperPlacement={popperPlacement}
                                            calendarClassName={showTime ? 'dateTimeCalendar' : undefined}
                                            showTimeSelect={showTime}
                                            timeIntervals={showTime ? 60 : undefined}
                                            timeCaption={showTime ? 'Time' : undefined}
                                        />
                                        {showClearButton ? (
                                            <IconButton
                                                form="icon"
                                                size="xs"
                                                color="neutral"
                                                icon="x"
                                                iconForm="regular"
                                                onClick={onClickClear}
                                            />
                                        ) : null}
                                        {isDisabled && (tooltipContext?.title || tooltipContext?.label) && (
                                            <Tooltip
                                                placement="bottom"
                                                title={tooltipContext?.title}
                                                label={tooltipContext?.label}
                                            >
                                                <IconButton
                                                    icon="question"
                                                    size="xs"
                                                    form="icon"
                                                    color="neutral"
                                                    className="tooltip-icon"
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            </DatePickerInputContainer>
                            {showErrorText && isMetaError && (
                                <Text scale="bodyS400" color="error">
                                    {error}
                                </Text>
                            )}
                        </ErrorContainer>
                    </StyledDatePickerContainer>
                )}
            </>
        )
    },
)

const FormikDatePicker: React.FC<BaseDatePickerProps> = ({
    name,
    onChange,
    isDisabled = false,
    showTime,
    showError = true, // set false if want to use external error state to control the error display
    validateByTouch = true,
    ...props
}) => {
    const [field, meta] = useField(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const showClearButton = !isDisabled && meta.value
    const isMetaError = showError && !!meta.error && (!validateByTouch || meta.touched)

    const date = Boolean(field.value)
        ? field.value && parseBackendDate(new Date(field.value).toISOString(), undefined, { showTime })
        : ''

    const onClickClear = (): void => {
        setFieldValue(field.name, null)
        setFieldTouched(field.name, true, false)
    }
    const onDatePickerChange = (date: Date | null) => {
        onChange
            ? onChange(date)
            : setFieldValue(field.name, date && parseBackendDate(date.toISOString(), undefined, { showTime }))
    }

    return (
        <DateInput
            {...field}
            {...props}
            name={name}
            isDisabled={isDisabled}
            showTime={showTime}
            isMetaError={isMetaError}
            showClearButton={showClearButton}
            date={date}
            onClickClear={onClickClear}
            onChange={onDatePickerChange}
            error={meta.error}
        />
    )
}

DateInput.displayName = 'DateInput'

export { FormikDatePicker }

export default DateInput
